import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ChevronUpIcon, ChevronDownIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';

import { useAuthStore } from '../../store/authStore';
import { useGlobalQueryStore } from '../../store/globalQueryStore';
import { useUserRoles } from '../../hooks/useUserRoles';
import { ROLES } from '../../config/routeConfig';
import { fetchLinkedAccounts, fetchAllBotRules } from '../../services/api';
import { MiniProgressIndicator } from './MiniProgressIndicator';
import { useUserContext } from '../../hooks/useUserContext';
import { UserAwareCacheKeys } from '../../utils/userAwareCacheKeys';

export const SetupChecklistBanner: React.FC = () => {
  const navigate = useNavigate();
  const { isApiAuthReady, isSetupComplete } = useAuthStore();
  const { isQueryingAllowed } = useGlobalQueryStore();
  const { roles: userRoles } = useUserRoles();
  const { userId, isReady: isUserContextReady } = useUserContext();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Determine user type for appropriate messaging
  const isTrialUser = userRoles.includes(ROLES.TRIAL);
  const isPaidUser = userRoles.some(role => 
    [ROLES.BASIC, ROLES.STANDARD, ROLES.ELITE].includes(role)
  );
  
  // Fetch linked accounts to check if user has any
  const { data: linkedAccounts, isLoading: isLoadingAccounts } = useQuery({
    queryKey: userId ? UserAwareCacheKeys.linkedAccounts(userId) : ['disabled'],
    queryFn: fetchLinkedAccounts,
    enabled: !!isApiAuthReady && isUserContextReady && !!userId && (isSetupComplete === false || isSetupComplete === null) && isQueryingAllowed,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch bot rules to check if user has any
  const { data: botRulesData, isLoading: isLoadingBotRules } = useQuery({
    queryKey: userId ? UserAwareCacheKeys.botRules(userId) : ['disabled'],
    queryFn: fetchAllBotRules,
    enabled: !!isApiAuthReady && isUserContextReady && !!userId && (isSetupComplete === false || isSetupComplete === null) && isQueryingAllowed,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Early return if setup is already complete - moved after hooks
  if (isSetupComplete === true) {
    return null;
  }
  
  // Determine what's missing
  const hasLinkedAccounts = linkedAccounts && linkedAccounts.length > 0;
  const hasBotRules = botRulesData && botRulesData.bots && botRulesData.bots.length > 0;
  
  // Check if setup is actually complete based on real data
  const isActuallyComplete = hasLinkedAccounts && hasBotRules;
  
  // Debug logging
  console.log('[SetupChecklistBanner] Setup status check:', {
    isSetupComplete,
    hasLinkedAccounts,
    hasBotRules,
    isActuallyComplete,
    accountsCount: linkedAccounts?.length || 0,
    botRulesCount: botRulesData?.bots?.length || 0
  });
  
  // Don't show banner while we're still loading data
  const isStillLoading = isLoadingAccounts || isLoadingBotRules;
  
  // Determine if the banner should render
  // Show banner ONLY if:
  // 1. API is ready AND
  // 2. We're not still loading data AND
  // 3. Our real-time check shows missing items (since isSetupComplete === true already handled by early return)
  const shouldRenderBanner = isApiAuthReady && 
                            !isStillLoading && 
                            !isActuallyComplete; // Only show if items are actually missing

  // Calculate progress steps based on real data
  const progressSteps = [
    {
      id: 1,
      label: 'Welcome',
      completed: true, // Always completed if they're seeing this banner
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      gradientFrom: 'from-blue-400',
      gradientTo: 'to-purple-500',
    },
    {
      id: 2,
      label: 'Connect Accounts',
      completed: !!hasLinkedAccounts,
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-purple-600',
    },
    {
      id: 3,
      label: 'Define Strategies',
      completed: !!hasBotRules,
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      gradientFrom: 'from-purple-500',
      gradientTo: 'to-pink-600',
    },
    {
      id: 4,
      label: 'Configure Engine & Complete',
      completed: false, // Never completed if banner is showing
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
        </svg>
      ),
      gradientFrom: 'from-indigo-500',
      gradientTo: 'to-blue-600',
    },
  ];

  // Handle navigation to onboarding
  const handleResumeSetup = () => {
    navigate('/onboarding', { 
      replace: false,
      state: { 
        resumeFromBanner: true
      }
    });
  };

  // Handle navigation to specific step
  const handleNavigateToStep = (stepId: number) => {
    navigate('/onboarding', { 
      replace: false,
      state: { 
        resumeFromBanner: true,
        targetStep: stepId
      }
    });
  };

  // Toggle function
  const toggleCollapse = () => setIsCollapsed(!isCollapsed);
  // --- Render Logic --- //

  // 1. Don't render if API not ready, still loading data, setup status unknown (null), or setup is complete (true)
  if (!shouldRenderBanner) {
    return null;
  }

  // 5. Render the actual banner content (simplified)
  return (
    <div className={`relative mx-4 z-30 ${isCollapsed ? 'mb-0' : '-mb-0'}`}>      {/* Dynamically set bottom margin, to -1rem when expanded */}
      {/* Banner Content Container with Tray Animation */}
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0, y: -20 }}
            animate={{ height: 'auto', opacity: 1, y: 0 }}
            exit={{ height: 0, opacity: 0, y: -20 }}
            transition={{ 
              duration: 0.4, 
              ease: [0.4, 0, 0.2, 1],
              opacity: { duration: 0.3 }
            }}
            className="bg-yellow-500/20 backdrop-blur-md border-yellow-500/50 border-l-4 border-l-yellow-400 text-white/95 rounded-md shadow-lg overflow-hidden"
            role="alert"
            id="setup-banner-content"
          >
            <div className="p-4">          <div className="flex items-center mb-2">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 text-yellow-400" />
            <p className="font-bold text-yellow-50">
              {isTrialUser ? "Welcome to your 14-day free trial!" : 
               isPaidUser ? "Let's get your dashboard set up!" : 
               "Action Required: Complete Your Dashboard Setup"}
            </p>
          </div>

          {/* Progress Indicator Section */}
          <div className="mb-4 p-3 bg-yellow-600/10 border border-yellow-500/30 rounded-md">
            <div className="flex items-center justify-between gap-3">
              <MiniProgressIndicator
                steps={progressSteps}
                className="flex-1"
                onStepClick={handleNavigateToStep}
              />
              <motion.button
                onClick={handleResumeSetup}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-400/40 rounded-md text-xs font-medium text-yellow-100 transition-colors duration-150 whitespace-nowrap"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Resume Setup</span>
                <ArrowRightIcon className="w-3.5 h-3.5" />
              </motion.button>
            </div>
          </div>

          {/* Updated dynamic message and links */}
          <p className="text-sm mb-2 text-yellow-100">Or complete these steps individually:</p>
          <p className="text-sm mb-2 text-yellow-100" style={{ display: 'none' }}>
            {isTrialUser ? "Complete the setup steps below to start tracking your trading bots. This usually takes less than 5 minutes." :
             isPaidUser ? "Complete the setup steps below to unlock your dashboard's full potential. This usually takes less than 5 minutes." :
             "Your dashboard setup is incomplete. Please complete the following:"}
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 pl-2 text-yellow-100">
            {!hasLinkedAccounts && (
              <li>
                Link your exchange accounts -{' '}
                <Link
                  to="/settings"
                  className="font-medium underline hover:text-yellow-50 focus:outline-none focus:ring-2 focus:ring-yellow-400/70 rounded"
                >
                  Go to Settings
                </Link>
              </li>
            )}
            {!hasBotRules && (
              <li>
                Define bot strategies and rules -{' '}
                <Link
                  to="/bot-maintenance"
                  className="font-medium underline hover:text-yellow-50 focus:outline-none focus:ring-2 focus:ring-yellow-400/70 rounded"
                >
                  Go to Bot Maintenance
                </Link>
              </li>
            )}
          </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsible Tab - Positioned relative to the outer container */}
      <motion.button
        onClick={toggleCollapse}
        className="absolute left-1/2 transform -translate-x-1/2
                   px-4 py-1 h-7 bg-yellow-700/60 backdrop-blur-sm
                   border border-yellow-600/80
                   rounded-md text-xs font-medium 
                   text-yellow-100
                   flex items-center justify-center gap-1
                   hover:bg-yellow-600/70
                   focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-gray-900
                   transition-colors duration-150 z-20"
        style={{ willChange: 'top, bottom' }}
        animate={{
          top: isCollapsed ? '0rem' : 'auto',
          bottom: isCollapsed ? 'auto' : '-1.5rem'
        }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        aria-expanded={!isCollapsed}
        aria-controls="setup-banner-content"
      >        {isCollapsed ? (
          <>
            <ExclamationTriangleIcon className="h-4 w-4 text-yellow-300" />
            <span>
              Setup Incomplete: {!hasLinkedAccounts && !hasBotRules ? 'Add Accounts & Bot Rules' : 
                               !hasLinkedAccounts ? 'Link Exchange Accounts' : 'Define Bot Rules'}
            </span>
            <ChevronUpIcon className="h-4 w-4 ml-1" />
          </>
        ) : (
          <>
            <span>Hide Setup Steps</span>
            <ChevronDownIcon className="h-4 w-4 ml-1" />
          </>
        )}
      </motion.button>
    </div>
  );
};
